import type { SiteRagReport } from '../../types/siteRag';
import { roadmapActionInputIdentity } from './actionDeduper';

/** A roadmap represents the latest recommendations, not every historical
 * revision. Retain a newer held report as a source rather than resurrecting
 * obsolete advice; this selector does not clear any report quality gate. */
export function roadmapSourceReports(reports: readonly SiteRagReport[]): SiteRagReport[] {
  const latest = new Map<string, SiteRagReport>();
  const timestamp = (report: SiteRagReport): number => {
    // Legacy stored bodies may have generatedAt, but it is not a canonical
    // ReportListItem field. Only a valid string may supply that fallback.
    const legacyGeneratedAt: unknown = (report as unknown as Record<string, unknown>).generatedAt;
    for (const stamp of [report.createdAt, legacyGeneratedAt]) {
      if (typeof stamp !== 'string') continue;
      const value = Date.parse(stamp);
      if (Number.isFinite(value)) return value;
    }
    return 0;
  };
  for (const report of reports) {
    const key = `${report.siteId}:${report.reportType}`;
    const prior = latest.get(key);
    if (!prior || timestamp(report) > timestamp(prior)
      || (timestamp(report) === timestamp(prior) && (report.id ?? '').localeCompare(prior.id ?? '') > 0)) {
      latest.set(key, report);
    }
  }
  return [...latest.values()];
}

/** Bind revision receipts and the advice being extracted across asynchronous
 * builders. Legacy reports can mutate under the same ID without a receipt.
 * Delivery, QA and readiness metadata can change without changing advice. */
export function roadmapSourceIdentity(reports: readonly SiteRagReport[]): string {
  const strings = (value: unknown, keys: readonly string[]): Array<string | null> => {
    const record = value && typeof value === 'object' ? value as Record<string, unknown> : undefined;
    return keys.map(key => typeof record?.[key] === 'string' ? record[key] as string : null);
  };
  const identities = roadmapSourceReports(reports).map(report => JSON.stringify([
    report.id, report.siteId, report.reportType, report._metadataOnly === true,
    strings(report, ['storagePath', 'reportRevisionId', 'sourcePath', 'sourceHash', 'documentPath']),
    strings(report.reportRevision, ['schemaVersion', 'revisionId', 'sourcePath', 'sourceHash', 'documentPath', 'documentHash']),
    strings(report.analysisRevision, ['schemaVersion', 'analysisId', 'siteId', 'domain', 'inputHash', 'contentHash', 'snapshotPath']),
    strings(report.reportContentRevision, ['schemaVersion', 'contentRevisionId', 'siteId', 'productId', 'reportId', 'reportRevisionId', 'inputHash', 'contentHash', 'snapshotPath']),
    roadmapActionInputIdentity(report),
  ])).sort();
  return JSON.stringify(identities);
}

/** Retired report sources require replacement, not an append to old advice. */
export function roadmapSourceReportDelta(
  existingReportIds: readonly string[],
  reports: readonly SiteRagReport[],
): { reports: SiteRagReport[]; newReports: SiteRagReport[]; retiredReportIds: string[] } {
  const selected = roadmapSourceReports(reports);
  const currentIds = new Set(selected.map(report => report.id).filter((id): id is string => Boolean(id)));
  const knownIds = new Set(existingReportIds);
  return {
    reports: selected,
    newReports: selected.filter(report => report.id && !knownIds.has(report.id)),
    retiredReportIds: [...knownIds].filter(id => !currentIds.has(id)),
  };
}
